// Row for earnings events.

import { useState } from "react";
import type { EarningResponse } from "../../types";
import { formatCompactNumber } from "../../utils/numbers";
import { Flag } from "../Flag";
import { ImportanceIcon } from "../ImportanceIcon";
import { Markdown } from "../Markdown";
import { TimeCell } from "../TimeCell";
import {
  computeRowClasses,
  getCategory,
  getImportance,
  hasDetail,
  useChangeAnimation,
  type CommonRowProps,
} from "../rowCommon";

interface Metric {
  label: string;
  title: string;
  value: string | number;
}

function MetricsList({ items }: { items: Metric[] }) {
  if (items.length === 0) return <>-</>;
  return (
    <dl className="metrics">
      {items.map((item) => (
        <div className="metric" key={item.label}>
          <dt title={item.title}>{item.label}:</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function EarningRow(props: CommonRowProps) {
  useChangeAnimation(props);
  const [expanded, setExpanded] = useState(false);

  const event = props.event as EarningResponse;
  const category = getCategory(props.event);
  const detail = event.detail;
  const location = category?.location || event.location || "";

  const actualItems: Metric[] = [];
  if (event.eps_actual !== null && event.eps_actual !== undefined) {
    actualItems.push({ label: "EPS", title: "Earnings Per Share", value: event.eps_actual });
  }
  if (event.revenue_actual !== null && event.revenue_actual !== undefined) {
    actualItems.push({
      label: "Rev",
      title: "Revenue",
      value: formatCompactNumber(event.revenue_actual),
    });
  }

  const previousItems: Metric[] = [];
  if (event.eps_previous !== null && event.eps_previous !== undefined) {
    previousItems.push({ label: "EPS", title: "Earnings Per Share", value: event.eps_previous });
  }
  if (event.revenue_previous !== null && event.revenue_previous !== undefined) {
    previousItems.push({
      label: "Rev",
      title: "Revenue",
      value: formatCompactNumber(event.revenue_previous),
    });
  }

  const detailSpan =
    (props.showLowHigh ? 8 : 6) + (props.chart === "mini" ? 1 : 0);

  return (
    <>
      <div className={computeRowClasses(props) + " calendar-grid-row"}>
        <div className="cell-location">
          <span className="flag-container">
            <Flag region={location} />
          </span>
        </div>
        <div className="cell-time">
          <TimeCell event={props.event} />
        </div>
        <div className="cell-title">
          {hasDetail(props.event) && (
            <button onClick={() => setExpanded((v) => !v)} className="expand-toggle">
              {expanded ? "▼" : "▶"}
            </button>
          )}
          <div className="info">
            <span className="earning-ticker">{event.ticker}</span>
            <span className="event-name"> {event.summary}</span>
            {event.weightings && (
              <span className="earning-weightings"> ({event.weightings})</span>
            )}
          </div>
        </div>
        <div className="cell-actual multi-line">
          <MetricsList items={actualItems} />
        </div>
        {props.chart === "mini" && <div className="cell-chart">&nbsp;</div>}
        <div className="cell-expected">-</div>
        {props.showLowHigh && (
          <>
            <div className="cell-low">-</div>
            <div className="cell-high">-</div>
          </>
        )}
        <div className="cell-previous multi-line">
          <MetricsList items={previousItems} />
        </div>
        <div className="cell-revised">-</div>
        <div className="cell-importance">
          <ImportanceIcon importance={getImportance(props.event)} size="md" />
        </div>
      </div>
      {expanded && detail && (
        <div className="detail-row calendar-grid-row">
          <div className="cell-location">&nbsp;</div>
          <div className="cell-time">&nbsp;</div>
          <div className="cell-title" style={{ gridColumn: `span ${detailSpan}` }}>
            <div className="detail-content">
              <Markdown content={detail} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EarningRow;
